import {
    ActionPermission,
    EndpointRegistry,
    extractActionPermission,
    ModulePermission,
    RegisteredHttpEndpoint,
} from '@luminovo/api-contracts';
import { useSuspenseHttpQuery } from '@luminovo/data-stack';
import { useCallback, useMemo } from 'react';

import { ActionPermissionTest, isAuthorizedForAction } from './isAuthorizedForAction';
import { isAuthorizedForModule, ModulePermissionTest } from './isAuthorizedForModule';

export const useModulePermissions = () => {
    const { data: modulePermissions } = useSuspenseHttpQuery(
        'GET /my/permissions',
        {},
        { staleTime: Infinity, select: (permissions) => permissions.module_permissions },
    );
    return modulePermissions;
};

/** Whether the organization checks access with module and action permissions rather than the token's. */
export const useIsAdvancedRoleManagementEnabled = (): boolean => {
    const { data: isAdvancedRoleManagementEnabled } = useSuspenseHttpQuery(
        'GET /my/feature-flags',
        {},
        { staleTime: Infinity, select: (flags) => flags.is_advanced_role_management_enabled },
    );
    return isAdvancedRoleManagementEnabled;
};

export type HasPermissionForEndpointFn<E extends RegisteredHttpEndpoint> = (
    ...args: Parameters<EndpointRegistry[E]['actionPermission']>
) => boolean;

export interface UseHasModulePermissions {
    hasModulePermissions: (requiredPermissions: ModulePermissionTest, options?: { fallback: boolean }) => boolean;

    hasPermissionForAction: (requiredPermissions: ActionPermissionTest, options?: { fallback: boolean }) => boolean;

    permissionForEndpoint: <T extends RegisteredHttpEndpoint>(
        endpoint: T,
        options?: { fallback: boolean },
    ) => { checkPermission: HasPermissionForEndpointFn<T> };
}

export const useHasModulePermissions: () => UseHasModulePermissions = () => {
    const {
        data: { module_permissions: modulePermissions, action_permissions: actionPermissions },
    } = useSuspenseHttpQuery('GET /my/permissions', {}, { staleTime: Infinity });
    const isAdvancedRoleManagementEnabled = useIsAdvancedRoleManagementEnabled();

    const { hasModulePermissions, hasPermissionForAction } = useMemo(
        () => modulePermissionChecks({ modulePermissions, actionPermissions, isAdvancedRoleManagementEnabled }),
        [modulePermissions, actionPermissions, isAdvancedRoleManagementEnabled],
    );

    const hasPermissionForEndpoint = useCallback(
        <T extends RegisteredHttpEndpoint>(
            endpoint: T,
            options: { fallback: boolean } = { fallback: true },
        ): { checkPermission: HasPermissionForEndpointFn<T> } => {
            const actionFn = extractActionPermission(endpoint);
            return {
                checkPermission: makeEndpointPermissionChecker(
                    actionFn,
                    isAdvancedRoleManagementEnabled,
                    hasPermissionForAction,
                    options,
                ),
            };
        },
        [isAdvancedRoleManagementEnabled, hasPermissionForAction],
    );

    return {
        hasModulePermissions,
        hasPermissionForAction,
        permissionForEndpoint: hasPermissionForEndpoint,
    };
};

/** {@link useHasModulePermissions}'s checks; with advanced role management off, each returns `fallback`. */
export function modulePermissionChecks({
    modulePermissions,
    actionPermissions,
    isAdvancedRoleManagementEnabled,
}: {
    modulePermissions: ModulePermission[];
    actionPermissions: ActionPermission[];
    isAdvancedRoleManagementEnabled: boolean;
}): Pick<UseHasModulePermissions, 'hasModulePermissions' | 'hasPermissionForAction'> {
    return {
        hasModulePermissions: (requiredPermissions, { fallback } = { fallback: true }) =>
            isAdvancedRoleManagementEnabled ? isAuthorizedForModule(modulePermissions, requiredPermissions) : fallback,
        hasPermissionForAction: (requiredPermissions, { fallback } = { fallback: true }) =>
            isAdvancedRoleManagementEnabled ? isAuthorizedForAction(actionPermissions, requiredPermissions) : fallback,
    };
}

const makeEndpointPermissionChecker = <F extends (...a: any[]) => ActionPermission | undefined>(
    fn: F,
    isAdvanced: boolean,
    hasPermissionForAction: (p: ActionPermissionTest, options?: { fallback: boolean }) => boolean,
    options: { fallback: boolean },
) => {
    return (...args: Parameters<F>) => {
        if (!isAdvanced) return options.fallback;
        const required = fn(...args); // args is a single tuple (Parameters<F>)
        if (required == null) return options.fallback;
        return hasPermissionForAction(required, options);
    };
};
